import { useEffect, useState } from 'react';
import type { SelectOption } from '../components/common/SearchableSelect/SearchableSelect';

const IBGE_STATES_URL = 'https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome';

function buildCitiesUrl(stateUf: string): string {
  return `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateUf}/municipios?orderBy=nome`;
}

export function useIbgeStates(enabled: boolean): { states: SelectOption[]; loading: boolean } {
  const [states, setStates] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setStates([]);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    fetch(IBGE_STATES_URL)
      .then((response) => response.json())
      .then((list: Array<{ sigla: string; nome: string }>) => {
        if (cancelled) {
          return;
        }

        setStates(list.map((state) => ({
          value: state.sigla,
          label: `${state.nome} (${state.sigla})`,
        })));
      })
      .catch(() => {
        if (!cancelled) {
          setStates([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { states, loading };
}

export function useIbgeCities(
  stateUf: string,
  enabled: boolean,
): { cities: SelectOption[]; loading: boolean } {
  const [cities, setCities] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !stateUf) {
      setCities([]);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    fetch(buildCitiesUrl(stateUf))
      .then((response) => response.json())
      .then((list: Array<{ nome: string }>) => {
        if (cancelled) {
          return;
        }

        setCities(list.map((city) => ({
          value: city.nome,
          label: city.nome,
        })));
      })
      .catch(() => {
        if (!cancelled) {
          setCities([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, stateUf]);

  return { cities, loading };
}
